import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MapPinIcon, SparklesIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { DeckResult } from '../../types/deck';
const EASE = [0.23, 1, 0.32, 1] as const;

/** Talab AI's verdict lines, as shown in the product's listing detail view. */
const AI_SUMMARY = ['Fair price based on current market comparison', 'GCC specifications confirmed', 'Mileage is reasonable for the model year', 'Seller response rate is high'];
interface DeckDetailModalProps {
  result: DeckResult;
  open: boolean;
  /** Which gallery photo is showing. */
  imageIndex: number;
  /** Drives the simulated scroll of the detail column. */
  scrolled?: boolean;
}

/**
 * ListingDetailModal, restaged inside the deck's Talab window: same backdrop,
 * gallery chrome, match pill and AI summary, recomposed side-by-side so it
 * reads at 16:9 presentation scale.
 */
export function DeckDetailModal({
  result,
  open,
  imageIndex,
  scrolled = false
}: DeckDetailModalProps) {
  const images = result.gallery ?? [result.image];
  const activeImage = images[Math.min(imageIndex, images.length - 1)];
  return <AnimatePresence data-id="element-1731" data-mp="14qhcaw6p7iqm">
      {open && <motion.div className="absolute inset-0 z-30 flex items-stretch p-3" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} data-id="element-1732" data-mp="zq33hbx9w4rj">
          <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" data-id="element-1733" data-mp="19nqz042pdgar" />

          <motion.div className="relative flex w-full overflow-hidden bg-white rounded-3xl shadow-2xl" initial={{
        opacity: 0,
        scale: 0.97,
        y: 14
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 14
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 320
      }} role="dialog" aria-modal="true" aria-label={result.title} data-id="element-1734" data-mp="1ne2t28iulp4j">
            {/* Gallery */}
            <div className="relative w-[56%] bg-slate-100 shrink-0" data-id="element-1735" data-mp="24g06glqtsrnn">
              <AnimatePresence mode="popLayout" data-id="element-1736" data-mp="29g4rmp2oiak7">
                <motion.img key={activeImage} src={activeImage} alt={result.title} className="absolute inset-0 w-full h-full object-cover" initial={{
              opacity: 0,
              scale: 1.03
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.5,
              ease: EASE
            }} data-id="element-1737" data-mp="29ea5j7fo8cej" />
              </AnimatePresence>

              <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-sm" data-id="element-1738" data-mp="2x5pbda6npbuj">
                <SparklesIcon className="w-3 h-3 text-ai-purple" data-id="element-1739" data-mp="xu6553cqmhok" />
                <span className="text-[10px] font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-1740" data-mp="12shb32zww81i">
                  {result.match}
                </span>
              </div>

              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/85 backdrop-blur-sm shadow-sm flex items-center justify-center" data-id="element-1741" data-mp="qflzycms5pvf">
                <ChevronLeftIcon className="w-4 h-4 text-slate-700" data-id="element-1742" data-mp="gjse22n68k0z" />
              </span>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/85 backdrop-blur-sm shadow-sm flex items-center justify-center" data-id="element-1743" data-mp="1e7axc7egl4j8">
                <ChevronRightIcon className="w-4 h-4 text-slate-700" data-id="element-1744" data-mp="195txmf7pno48" />
              </span>

              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-id="element-1745" data-mp="14aochim4udh7">
                {images.map((image, index) => <motion.span key={image} className="h-1.5 rounded-full bg-white" animate={{
              width: index === imageIndex ? 16 : 6,
              opacity: index === imageIndex ? 1 : 0.5
            }} transition={{
              duration: 0.3,
              ease: EASE
            }} data-id="element-1746" data-mp="3ozc4ktwgjocc" data-mpi={index} />)}
              </div>

              <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-dark-navy/70 text-white text-[9.5px] font-medium" data-id="element-1747" data-mp="12rech0zmvl4p">
                {Math.min(imageIndex + 1, images.length)} / {images.length}
              </div>
            </div>

            {/* Detail column */}
            <div className="relative flex-1 min-w-0 overflow-hidden" data-id="element-1748" data-mp="20en1h5yufcbg">
              <span className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm shadow-sm flex items-center justify-center" data-id="element-1749" data-mp="1lguretc2fg4n">
                <XIcon className="w-4 h-4 text-slate-700" data-id="element-1750" data-mp="1qemftddi4k43" />
              </span>

              <motion.div className="p-5 pr-12" animate={{
            y: scrolled ? -78 : 0
          }} transition={{
            duration: 1.1,
            ease: EASE
          }} data-id="element-1751" data-mp="a1edtjvq74aa">
                <h3 className="font-display font-bold text-[17px] text-dark-navy leading-tight" data-id="element-1752" data-mp="2t7qnyma5dquy">
                  {result.title}
                </h3>
                <div className="flex items-center gap-1.5 mt-1.5 text-slate-500" data-id="element-1753" data-mp="2objdcg6t3r6o">
                  <MapPinIcon className="w-3 h-3" data-id="element-1754" data-mp="328ok6iecgu2z" />
                  <span className="text-[11px]" data-id="element-1755" data-mp="2y73992ldepvf">{result.meta}</span>
                </div>
                <p className="mt-2.5 font-display font-bold text-[20px] text-primary-indigo leading-none" data-id="element-1756" data-mp="2ahsgo8e6c1sd">
                  {result.price}
                  {result.priceNote && <span className="text-[11px] font-medium text-slate-400" data-id="element-1757" data-mp="25h4fitw8e4kf">
                      {result.priceNote}
                    </span>}
                </p>

                {result.specs && <div className="grid grid-cols-2 gap-2 mt-4" data-id="element-1758" data-mp="1qtt1mlcku8h5">
                    {[...result.specs, {
                label: 'Transmission',
                value: 'Automatic'
              }, {
                label: 'Seats',
                value: '5'
              }].map(spec => <div key={spec.label} className="bg-slate-50 rounded-lg px-2.5 py-1.5" data-id="element-1759" data-mp="3qw118l1z5n9a">
                        <p className="text-[8px] uppercase tracking-[0.12em] text-slate-400" data-id="element-1760" data-mp="3vn0v1hfsrp7k">
                          {spec.label}
                        </p>
                        <p className="text-[11px] font-semibold text-dark-navy" data-id="element-1761" data-mp="4g6gqhw30o8j">{spec.value}</p>
                      </div>)}
                  </div>}

                <div className="mt-4" data-id="element-1762" data-mp="37y7zhm337jdh">
                  <p className="flex items-center gap-1.5 text-[9.5px] font-semibold uppercase tracking-[0.16em] text-slate-400" data-id="element-1763" data-mp="37z9jig22zg7c">
                    <SparklesIcon className="w-3 h-3 text-ai-purple" data-id="element-1764" data-mp="3hw5nq9ky5g63" /> Talab AI summary
                  </p>
                  <ul className="mt-2 space-y-1.5" data-id="element-1765" data-mp="s73odigugzdz">
                    {AI_SUMMARY.map(line => <li key={line} className="flex items-start gap-2" data-id="element-1766" data-mp="x4c65hy2z1ep">
                        <CheckCircle2Icon className="w-3 h-3 mt-[2px] text-success shrink-0" data-id="element-1767" data-mp="2hn66gp1ejbtd" />
                        <span className="text-[11px] leading-snug text-slate-600" data-id="element-1768" data-mp="2col5uoi8mq4e">{line}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="flex items-center gap-2 mt-4" data-id="element-1769" data-mp="22yjgzui293of">
                  <span className="flex-1 h-8 rounded-xl bg-gradient-to-r from-primary-indigo to-ai-purple text-white text-[11px] font-semibold flex items-center justify-center" data-id="element-1770" data-mp="22y07n26h7nm0">
                    Book Now
                  </span>
                  <span className="w-8 h-8 rounded-xl border border-slate-200 flex items-center justify-center" data-id="element-1771" data-mp="1oyqts95qt5l7">
                    <MessageSquareIcon className="w-3.5 h-3.5 text-slate-500" data-id="element-1772" data-mp="1jzxfwfmjncki" />
                  </span>
                  <span className="w-8 h-8 rounded-xl border border-slate-200 flex items-center justify-center" data-id="element-1773" data-mp="3jdbakaqy0gzb">
                    <PhoneIcon className="w-3.5 h-3.5 text-slate-500" data-id="element-1774" data-mp="3edf6ycm5qrka" />
                  </span>
                </div>

                <p className="mt-3 text-[10px] leading-relaxed text-slate-400" data-id="element-1775" data-mp="2n9nkp3hsln6b">
                  Free cancellation up to 24 hours before pick-up · Unlimited kilometres ·
                  Comprehensive insurance included · Delivery available in Dubai.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}